<template>
  <div v-if="failed" class="load-state load-state-failed" role="alert">
    <p class="load-state-msg">{{ message }}</p>
    <button type="button" class="load-state-retry" @click="emit('retry')">Try again</button>
  </div>
  <div v-else-if="loading && shown" class="load-state load-state-loading" role="status" aria-live="polite">
    <span class="load-state-dots" aria-hidden="true"><i></i><i></i><i></i></span>
    <span class="load-state-sr">{{ loadingLabel }}</span>
  </div>
</template>

<script setup>
// One quiet loading / failed state for any list that fetches data.
//   loading: gold dots, shown only after `delay` ms so fast loads never flash
//   failed : one kind line + "Try again" (emits `retry`)
// The empty state stays with the list (its copy is specific to each list).
import { ref, watch, onBeforeUnmount } from 'vue'

const props = defineProps({
  loading: { type: Boolean, default: false },
  failed: { type: Boolean, default: false },
  message: { type: String, default: "Couldn't load this just now." },
  loadingLabel: { type: String, default: 'Loading' },
  delay: { type: Number, default: 250 },
})
const emit = defineEmits(['retry'])

const shown = ref(false)
let timer = null
function clear() { if (timer) { clearTimeout(timer); timer = null } }

watch(() => props.loading, (on) => {
  clear()
  if (!on) { shown.value = false; return }
  if (props.delay <= 0) { shown.value = true; return }
  timer = setTimeout(() => { shown.value = true; timer = null }, props.delay)
}, { immediate: true })

onBeforeUnmount(clear)
</script>

<style scoped>
.load-state { display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; padding: 28px 16px; text-align: center; }
.load-state-dots { display: inline-flex; gap: 6px; }
.load-state-dots i { width: 8px; height: 8px; border-radius: 50%; background: #FFDFA3;
  animation: load-state-pulse 1.2s ease-in-out infinite; }
.load-state-dots i:nth-child(2) { animation-delay: 0.2s; }
.load-state-dots i:nth-child(3) { animation-delay: 0.4s; }
@keyframes load-state-pulse {
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1); }
}
.load-state-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.load-state-msg { margin: 0; font-size: 14px; color: rgba(195, 169, 255, 0.75); }
.load-state-retry { min-height: 44px; min-width: 44px; padding: 0 16px; background: none; border: none;
  color: #FFDFA3; font-size: 14px; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.load-state-retry:hover { color: #f0d9a3; }
@media (prefers-reduced-motion: reduce) { .load-state-dots i { animation: none; opacity: 0.8; } }
</style>
